/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List } from 'semantic-ui-react';

import { sxtheme } from 'utils/cssHelpers';
import { getDateStrToday, isReportDateUrlDeeplinked } from 'utils/dateTimeHelpers';
import { isMobile } from 'utils/responsive';

import { Typography } from 'components/_common/Typography';
import BdButton from 'components/_controls/BdButton/BdButton';
import { ViewPanel } from 'components/_controls/ViewPanel';

import diseaseActivityDetailsSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityDetails.selectors';
import {
  fetchDiseaseActivityUpdates,
  fetchNewerDiseaseActivityUpdates,
  fetchOlderDiseaseActivityUpdates,
} from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.saga';
import diseaseActivityUpdatesSelectors from 'diseaseActivitiesInCountryLocations/diseaseActivityUpdates.selectors';
import { closePanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import { minimizePanel, PanelType } from 'diseaseActivityPanels/diseaseActivityPanels.slice';
import locationsSelectors from 'locations/locations.selectors';

import DiseaseActivityUpdateCard from './DiseaseActivityUpdates/DiseaseActivityUpdateCard';
import DiseaseActivityUpdatesToolbar from './DiseaseActivityUpdates/DiseaseActivityUpdatesToolbar';

const DiseaseActivityUpdates: React.FC = () => {
  const dispatch = useDispatch();
  const isMobileDevice = isMobile(useBreakpointIndex());

  const isLoading = useSelector(diseaseActivityUpdatesSelectors.isLoading);
  const hasError = useSelector(diseaseActivityUpdatesSelectors.hasError);
  const isLoadingAbove = useSelector(diseaseActivityUpdatesSelectors.isLoadingAbove);
  const isLoadingBelow = useSelector(diseaseActivityUpdatesSelectors.isLoadingBelow);
  const activePanel = useSelector(diseaseActivityPanelsSelectors.activePanel);
  const historyDeeplinkDate = useSelector(diseaseActivityPanelsSelectors.getHistoryDeeplinkDate);

  const showProximalHistoryUpdates = useSelector(
    diseaseActivityPanelsSelectors.showProximalHistoryUpdates
  );
  const isCountrySpecific = useSelector(diseaseActivityPanelsSelectors.isHistoryCountrySpecific);
  const countryName = useSelector(diseaseActivityDetailsSelectors.countryName);
  const dailyReports = useSelector(diseaseActivityUpdatesSelectors.dailyReports);
  const hasNewerEntries = useSelector(diseaseActivityUpdatesSelectors.hasNewerEntries);
  const hasOlderEntries = useSelector(diseaseActivityUpdatesSelectors.hasOlderEntries);

  const filter = useSelector(diseaseActivityUpdatesSelectors.filter);
  const userLocationIds = useSelector(locationsSelectors.getAllLocationIds);
  const getSelectedLocationTitleShort = useSelector(
    locationsSelectors.getSelectedLocationTitleShort
  );
  const selectedLocationCountry = useSelector(locationsSelectors.getSelectedLocationCountryName);
  const isSelectedLocationCountry = useSelector(locationsSelectors.isSelectedLocationCountry);

  const { canClose, isMinimized, isOpen } = useSelector(
    diseaseActivityPanelsSelectors.getPanelVisibility(PanelType.DiseaseActivityUpdatesPanel)
  );

  const panelTitle = hasError
    ? 'Something went wrong'
    : isCountrySpecific && countryName
    ? `Activity History in ${countryName}`
    : showProximalHistoryUpdates && selectedLocationCountry
    ? `Activity History in ${selectedLocationCountry}`
    : !isCountrySpecific && !showProximalHistoryUpdates
    ? 'Global Activity History'
    : 'Loading...';

  const onMinimize = (flag: boolean): void => {
    dispatch(minimizePanel(PanelType.DiseaseActivityUpdatesPanel, flag));
  };

  const loadBelow = (): void => {
    dispatch(
      fetchOlderDiseaseActivityUpdates({
        activityDate:
          dailyReports.length > 0
            ? dailyReports[dailyReports.length - 1]?.reportDate || getDateStrToday()
            : getDateStrToday(),
        excludeCaseCounts: filter === 'with-updates-only',
      })
    );
  };
  const loadAbove = (): void => {
    dispatch(
      fetchNewerDiseaseActivityUpdates({
        activityDate: dailyReports[0]?.reportDate || getDateStrToday(),
        excludeCaseCounts: filter === 'with-updates-only',
      })
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    dispatch(
      fetchDiseaseActivityUpdates({
        excludeCaseCounts: filter === 'with-updates-only',
      })
    );
  }, [filter]);

  if (isMobileDevice && activePanel !== PanelType.DiseaseActivityUpdatesPanel) {
    return null;
  }

  return (
    isOpen && (
      <ViewPanel
        grayBG
        title={panelTitle}
        isLoading={isLoading}
        hasError={hasError}
        onRetryClick={() =>
          dispatch(
            fetchDiseaseActivityUpdates({
              excludeCaseCounts: filter === 'with-updates-only',
            })
          )
        }
        canClose={canClose}
        onClose={() => dispatch(closePanel(PanelType.DiseaseActivityUpdatesPanel))}
        toolbar={<DiseaseActivityUpdatesToolbar />}
        scrollToTopButtonThresh={hasNewerEntries ? undefined : 600}
        isMinimized={isMinimized}
        onMinimize={onMinimize}
      >
        {dailyReports?.length ? (
          <div
            sx={{
              p: '16px',
              bg: sxtheme(t => t.colors.deepSea10),
              minHeight: '100%',
            }}
          >
            {hasNewerEntries && (
              <div sx={{ mb: '16px', textAlign: 'center' }}>
                {isLoadingAbove && (
                  <Typography variant="subtitle2" color="sea50">
                    Loading...
                  </Typography>
                )}
                {!isLoadingAbove && (
                  <BdButton type="tertiary" onClick={loadAbove} text="Load 10 newer" small />
                )}
              </div>
            )}
            <List className="xunpadded">
              {dailyReports?.map(x => {
                const isActive = isReportDateUrlDeeplinked(x.reportDate, historyDeeplinkDate);
                const articles = [
                  ...(x.caseCounts?.locationCaseCounts?.articles ?? []),
                  ...(x.caseCounts?.countryCaseCounts?.articles ?? []),
                ];

                return (
                  <DiseaseActivityUpdateCard
                    key={x.reportDate}
                    reportDate={x.reportDate}
                    countryDescriptions={x.countryDescriptions}
                    totalSourceCount={x.articleCount}
                    userLocationIds={userLocationIds}
                    statLHS={
                      showProximalHistoryUpdates && !isSelectedLocationCountry
                        ? {
                            label: `Reported cases in ${getSelectedLocationTitleShort}`,
                            stat: x.caseCounts?.locationCaseCounts?.caseCounts?.reportedCases,
                            sources: articles,
                          }
                        : showProximalHistoryUpdates && isSelectedLocationCountry
                        ? null
                        : {
                            label: isCountrySpecific ? 'Reported cases' : 'Global reported cases',
                            stat: isCountrySpecific
                              ? x.caseCounts?.locationCaseCounts?.caseCounts?.reportedCases
                              : x.caseCounts?.globalCaseCounts?.reportedCases,
                            sources: isCountrySpecific ? articles : null,
                          }
                    }
                    statRHS={
                      showProximalHistoryUpdates
                        ? {
                            label: `Reported cases in ${selectedLocationCountry}`,
                            stat: x.caseCounts?.countryCaseCounts?.caseCounts?.reportedCases,
                            sources: articles,
                          }
                        : {
                            label: isCountrySpecific ? 'Reported deaths' : 'Global reported deaths',
                            stat: isCountrySpecific
                              ? x.caseCounts?.locationCaseCounts?.caseCounts?.deaths
                              : x.caseCounts?.globalCaseCounts?.deaths,
                            sources: isCountrySpecific ? articles : null,
                          }
                    }
                    isActive={isActive}
                    isScrollToTarget={isActive}
                  />
                );
              }) || 'No data'}
            </List>
            <div sx={{ mt: '16px', textAlign: 'center' }}>
              {hasOlderEntries && !isLoadingBelow ? (
                <BdButton type="tertiary" onClick={loadBelow} text="Load 10 more" small />
              ) : hasOlderEntries ? (
                <Typography variant="subtitle2" color="sea50">
                  Loading...
                </Typography>
              ) : (
                <Typography variant="body2" color="stone90" sx={{ textAlign: 'center' }}>
                  You&apos;ve reached the end of the list.
                </Typography>
              )}
            </div>
          </div>
        ) : (
          <div
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <div sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="stone70">
                No Data
              </Typography>
              <Typography variant="body1" color="stone70">
                There is no activity with situational updates
              </Typography>
            </div>
          </div>
        )}
      </ViewPanel>
    )
  );
};

export default DiseaseActivityUpdates;
